import React, { FC, useMemo, useEffect, useRef, useCallback, useState } from 'react';
import FullCalendar, { CalendarRef } from '@fullcalendar/react';
import moment from 'moment';
import {
	formatSchedulerTime,
	formatSchedulerTimeRange,
} from 'shared/utils/dates/formatSchedulerTime';
import { isTimedTimeOff } from '../../utils/timeOffRequestForm';
import { resolveShiftEndTime } from '../../utils/shiftValidation';
import { getWeekViewDropTimes } from '../../utils/shiftDragDrop';
import {
	isDepartmentHeaderResourceId,
	hasDepartmentGroupedResources,
	parseWeekViewEmployeeId,
} from '../../utils/scheduleResourceUtils';
import {
	applyScheduleEmployeeOrder,
	getEmployeeOrderIds,
	getScheduleEmployeeOrder,
	reorderScheduleEmployeeIds,
	saveScheduleEmployeeOrder,
} from '../../utils/scheduleEmployeeOrder';
import { DEFAULT_START_OF_WEEK, getDayOfWeekIndex } from '../../utils/scheduleNavigation';
import { MbscCalendarEvent } from '@mobiscroll/react';
import { CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import { useQuery } from 'react-query';

import { fetchAllEventTypes } from 'core/API/events';
import {
	getEnrichedWorkplaceUsers,
	assignmentWorkplaceUserOptions,
} from 'core/utils/enrichWorkplaceUsers';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { IWorkplaces } from 'module/workplaces/interface/WorkplacesInterface';
import { ScheduleResource, EventTypeResource } from 'types/scheduler';
import { Holiday } from 'types/holidays';
import { useCalendarView } from 'core/hooks/useCalendarView';
import { FULLCALENDAR_LICENSE_KEY, fullCalendarPlugins } from 'core/config/fullCalendarSetup';
import { fullCalendarLegacyClassOptions, joinFcClass } from 'core/config/fullCalendarCompat';
import {
	ScheduleEvent,
	ScheduleResource as ScheduleResourceComponent,
} from './ScheduleEvent.component';
import ScheduleEventsHeader from './ScheduleEventsHeader';
import './DesktopPage.style.scss';
import './FullCalendarSchedule.scss';

const FULLSCREEN_SPACER_RESOURCE_ID = '__schedule-fullscreen-spacer__';

const isFullscreenSpacerResourceId = (resourceId?: string | number | null) =>
	String(resourceId || '') === FULLSCREEN_SPACER_RESOURCE_ID;

// Interactive controls rendered inside a shift card that must not open the quick edit dialog
const SHIFT_CARD_CONTROL_SELECTOR = '.action-buttons, .employee-status-indicator';

interface IFullCalendarSchedule {
	openEditShiftModal: (args: any) => void;
	openAddShiftModal: (args: any) => void;
	allEvents: MbscCalendarEvent[];
	holidays?: Holiday[];
	workplacesData: IWorkplaces;
	updateTimeRange: (firstday: Date, lastday: Date) => void;
	setCalendarType: (args: 'schedule' | 'request' | 'event') => void;
	calendarType: string;
	openRequestsModal: (args: any) => void;
	openAddEventModal: (args: any) => void;
	openEditEventModal: (args: any) => void;
	refetch?: any;
	dragTimeStep?: number;
	resourcesOverride?: ScheduleResource[];
	renderHeaderOverride?: () => React.ReactElement | null;
	headerStartContent?: React.ReactNode;
	startOfDay?: string;
	startOfWeek?: string;
	workplaceWorkingHoursStart?: string;
	selectedDate?: Date;
	businessWeekStart?: string;
	onEditShift?: (event: MbscCalendarEvent) => void;
	onTimeOffClick?: (event: MbscCalendarEvent) => void;
	onDeleteShift?: (event: MbscCalendarEvent) => void;
	onCopyShift?: (event: MbscCalendarEvent) => void;
	onPublish?: () => void;
	canManageShifts?: boolean;
	canDeleteShifts?: boolean;
	canManageAllRequests?: boolean;
	canReorderEmployeeRows?: boolean;
	scheduleUserId?: string | number;
	isShiftActionsEnabled?: boolean;
	isShiftsLoading?: boolean;
	onShiftDragUpdate?: (event: any) => boolean | void;
	onCreateShiftByDrag?: (args: { start: Date; end: Date; resourceId: string }) => void;
	defaultShiftLengthHours?: number;
	minShiftLength?: number;
}

const FullCalendarSchedule: FC<IFullCalendarSchedule> = ({
	openEditShiftModal: _openEditShiftModal,
	openAddShiftModal,
	allEvents,
	holidays: _holidays = [],
	workplacesData,
	updateTimeRange: _updateTimeRange,
	setCalendarType,
	calendarType,
	openRequestsModal: _openRequestsModal,
	openEditEventModal: _openEditEventModal,
	refetch: _refetch,
	dragTimeStep,
	resourcesOverride,
	renderHeaderOverride,
	headerStartContent,
	startOfDay = '00:00',
	startOfWeek,
	workplaceWorkingHoursStart,
	selectedDate,
	businessWeekStart,
	onEditShift,
	onTimeOffClick,
	onDeleteShift,
	onCopyShift,
	onPublish,
	canManageShifts = false,
	canDeleteShifts = false,
	canManageAllRequests = false,
	canReorderEmployeeRows: canReorderEmployeeRowsProp = false,
	scheduleUserId,
	isShiftActionsEnabled = true,
	isShiftsLoading = false,
	onShiftDragUpdate,
	onCreateShiftByDrag,
	defaultShiftLengthHours,
	minShiftLength,
}) => {
	const { workplaceId } = useWorkplacesContext();
	const { calendarView } = useCalendarView();
	const theme = useTheme();
	const isTabletViewport = useMediaQuery(theme.breakpoints.down('lg'));
	const calendarRef = useRef<CalendarRef>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const toolbarRef = useRef<HTMLDivElement>(null);
	const calendarBodyRef = useRef<HTMLDivElement>(null);
	const canInteractWithShifts = canManageShifts && isShiftActionsEnabled;
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [isFullscreenSupported, setIsFullscreenSupported] = useState(false);
	const [employeeRowOrder, setEmployeeRowOrder] = useState<string[] | null>(null);
	const employeeRowOrderRef = useRef<string[] | null>(null);
	const isWeekLikeView = calendarView === 'week' || calendarView === 'workweek';

	useEffect(() => {
		if (!workplaceId || !scheduleUserId) {
			setEmployeeRowOrder(null);
			return;
		}

		setEmployeeRowOrder(getScheduleEmployeeOrder(workplaceId, scheduleUserId));
	}, [workplaceId, scheduleUserId]);

	useEffect(() => {
		employeeRowOrderRef.current = employeeRowOrder;
	}, [employeeRowOrder]);

	const { isLoading: isLoadingUsersWorkplace } = useQuery(
		['workplace-employees', workplaceId],
		() => getEnrichedWorkplaceUsers(workplaceId, assignmentWorkplaceUserOptions),
		{
			staleTime: 300000,
			enabled: !!workplaceId,
		}
	);

	const { data: eventTypes } = useQuery(['eventTypes'], () => fetchAllEventTypes(), {
		enabled: calendarType === 'event',
	});

	const eventResources = useMemo<EventTypeResource[]>(() => {
		const eventsListResource = eventTypes?.items?.map((eventType: any): EventTypeResource => {
			return {
				id: eventType.id,
				name: eventType.type,
			};
		});

		return eventsListResource ?? [];
	}, [eventTypes]);

	const baseResources = useMemo(
		() => resourcesOverride ?? (eventResources.length ? eventResources : []),
		[resourcesOverride, eventResources]
	);
	const hasDepartmentGrouping = useMemo(
		() => hasDepartmentGroupedResources(baseResources),
		[baseResources]
	);
	const canReorderEmployeeRows =
		isWeekLikeView &&
		calendarType === 'schedule' &&
		canReorderEmployeeRowsProp &&
		isShiftActionsEnabled &&
		!hasDepartmentGrouping;

	const compactSizing = useMemo(() => {
		if (isWeekLikeView) {
			return {
				resourceAreaWidth: isTabletViewport ? '132px' : '180px',
				slotMinWidth: isTabletViewport ? 120 : 180,
				eventMinWidth: isTabletViewport ? 100 : 140,
			};
		}

		return {
			resourceAreaWidth: isTabletViewport ? '120px' : '170px',
			slotMinWidth: isTabletViewport ? 36 : 42,
			eventMinWidth: isTabletViewport ? 28 : 32,
		};
	}, [isWeekLikeView, isTabletViewport]);

	// Week view uses full-day display slots; 15-minute snap causes invalid drop targets.
	const calendarSnapDuration = isWeekLikeView
		? '24:00:00'
		: `00:${String(dragTimeStep || 15).padStart(2, '0')}:00`;

	const toggleFullscreen = useCallback(async () => {
		if (!isFullscreenSupported || !containerRef.current) return;

		try {
			if (document.fullscreenElement) {
				await document.exitFullscreen();
				return;
			}

			await containerRef.current.requestFullscreen();
		} catch (_error) {
			setIsFullscreen(false);
		}
	}, [isFullscreenSupported]);

	// Convert resources to FullCalendar format
	const fullCalendarResources = useMemo(() => {
		const resources: ScheduleResource[] = [...baseResources];

		const orderedResources =
			canReorderEmployeeRows && resources.length > 0
				? applyScheduleEmployeeOrder(resources, employeeRowOrder)
				: resources;

		const mappedResources = orderedResources.map((resource, index) => {
			const sortOrder =
				String(resource.id) === 'timeoff'
					? -1
					: 'sortOrder' in resource
						? ((resource as { sortOrder?: number }).sortOrder ?? index)
						: index;

			return {
				id: String(resource.id),
				title: resource.name,
				sortOrder,
				extendedProps: {
					...resource,
					sortOrder,
				},
			};
		});

		if (!isFullscreen || mappedResources.length === 0) {
			return mappedResources;
		}

		return [
			...mappedResources,
			{
				id: FULLSCREEN_SPACER_RESOURCE_ID,
				title: '',
				sortOrder: Number.MAX_SAFE_INTEGER,
				extendedProps: {
					isFullscreenSpacer: true,
					sortOrder: Number.MAX_SAFE_INTEGER,
				},
			},
		];
	}, [baseResources, canReorderEmployeeRows, employeeRowOrder, isFullscreen]);

	const getDepartmentHeaderClass = useCallback(
		(arg: { resource?: { id: string; extendedProps?: any } | null }) => {
			const resource = arg.resource;
			if (!resource) return '';
			if (resource.extendedProps?.isFullscreenSpacer || isFullscreenSpacerResourceId(resource.id)) {
				return 'is-fullscreen-spacer-row';
			}
			if (String(resource.id) === 'timeoff') {
				return 'is-timeoff-row';
			}
			if (resource.extendedProps?.isDepartmentHeader || isDepartmentHeaderResourceId(resource.id)) {
				return 'is-department-header-row';
			}
			return '';
		},
		[]
	);

	const handleEmployeeRowReorder = useCallback(
		(draggedId: string, targetId: string) => {
			if (!workplaceId || !scheduleUserId || !resourcesOverride?.length) return;

			const currentOrder = getEmployeeOrderIds(resourcesOverride, employeeRowOrderRef.current);
			const nextOrder = reorderScheduleEmployeeIds(currentOrder, draggedId, targetId);
			if (nextOrder.join('|') === currentOrder.join('|')) return;

			employeeRowOrderRef.current = nextOrder;
			setEmployeeRowOrder(nextOrder);
			saveScheduleEmployeeOrder(workplaceId, scheduleUserId, nextOrder);
		},
		[resourcesOverride, scheduleUserId, workplaceId]
	);

	const handleEmployeeRowReorderRef = useRef(handleEmployeeRowReorder);
	handleEmployeeRowReorderRef.current = handleEmployeeRowReorder;

	// While a shift is being dragged/resized, new event data (refetches from earlier drops, window
	// focus, interval) must not replace FullCalendar's store under the pointer. It is applied once
	// the interaction ends.
	const isEventInteractionActiveRef = useRef(false);
	const lastFullCalendarEventsRef = useRef<any[]>([]);
	const [interactionReleaseVersion, setInteractionReleaseVersion] = useState(0);

	const handleEventInteractionStart = useCallback(() => {
		isEventInteractionActiveRef.current = true;
	}, []);

	const handleEventInteractionStop = useCallback(() => {
		// eventDrop/eventResize fire after the stop callback; let them apply their optimistic
		// update before the calendar picks up fresh props again.
		window.setTimeout(() => {
			isEventInteractionActiveRef.current = false;
			setInteractionReleaseVersion((version) => version + 1);
		}, 0);
	}, []);

	// Convert events to FullCalendar format
	const fullCalendarEvents = useMemo(() => {
		if (isEventInteractionActiveRef.current) return lastFullCalendarEventsRef.current;

		const events = allEvents.map((event: any) => {
			const timeRange = formatSchedulerTimeRange(event.start, event.end);

			// For week view, normalize to day boundaries so events occupy full day slot
			let displayStart = event.start;
			let displayEnd = event.end;

			if (calendarView === 'week' || calendarView === 'workweek') {
				displayStart = moment(event.start).startOf('day').toDate();
				if (event.isTimeOff) {
					// Time off can span multiple days — keep the full inclusive range
					// (exclusive end = start of the day after the last selected day)
					displayEnd = moment(event.end).startOf('day').add(1, 'day').toDate();
				} else {
					// Shifts: full-day slot based on start date only
					displayEnd = moment(event.start).add(1, 'day').startOf('day').toDate();
				}
			} else if (
				event.isTimeOff &&
				!isTimedTimeOff(event.startAt, event.endAt, event.timeOffRequest?.allDay)
			) {
				// Hourly day view needs an exclusive end at the next midnight
				// so a full-day block is not collapsed to the default 1-hour duration.
				displayStart = moment(event.start).startOf('day').toDate();
				displayEnd = moment(event.end).startOf('day').add(1, 'day').toDate();
			}

			const status = event.employeeStatusData;
			// Include a status fingerprint so FullCalendar refreshes warning icons when OT changes
			const statusKey = status
				? [
						status.isShiftCausingDailyOT,
						status.isShiftCausingDailyApproach,
						status.isShiftCausingWeeklyOT,
						status.isShiftCausingWeeklyApproach,
						status.hasAvailabilityIssue,
						status.isMinor,
						status.todayHours,
						status.totalHours,
					].join('|')
				: 'none';

			return {
				id: event.shift_id || event.id,
				resourceId: String(event.resource),
				title: event.title || '',
				start: displayStart,
				end: displayEnd,
				color: event.isTimeOff ? 'transparent' : event.background_color || event.color,
				contrastColor: event.isTimeOff ? '#ffffff' : event.text_color || '#000',
				editable: canInteractWithShifts && event.editable !== false && !event.isTimeOff,
				startEditable: canInteractWithShifts && event.editable !== false && !event.isTimeOff,
				durationEditable: canInteractWithShifts && event.editable !== false && !event.isTimeOff,
				extendedProps: {
					...event,
					// Ensure startAt and endAt are preserved for ScheduleEvent component
					startAt: event.startAt || event.start,
					endAt: event.endAt || event.end,
					timeRange, // Add formatted time range for display
					actualStart: event.start, // Preserve actual times for operations
					actualEnd: event.end,
					statusKey,
				},
			};
		});

		lastFullCalendarEventsRef.current = events;
		return events;
		// interactionReleaseVersion re-runs this once a drag/resize ends
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allEvents, canInteractWithShifts, calendarView, interactionReleaseVersion]);

	// Calculate visible date range based on business week
	const visibleRange = useMemo(() => {
		if (!businessWeekStart) return undefined;

		const start = moment(businessWeekStart).toDate();
		const end = moment(businessWeekStart).add(7, 'days').toDate();

		return { start, end };
	}, [businessWeekStart]);

	const firstDayIndex = getDayOfWeekIndex(startOfWeek || DEFAULT_START_OF_WEEK);

	const calendarInitialDate = useMemo(() => {
		if (!businessWeekStart) return undefined;
		return moment(businessWeekStart).toDate();
	}, [businessWeekStart]);

	// Keep FullCalendar's visible week aligned with the selector / business week anchor
	useEffect(() => {
		if (!businessWeekStart || !calendarRef.current) return;
		const calendarApi = calendarRef.current.getApi();
		calendarApi.gotoDate(moment(businessWeekStart).toDate());
	}, [businessWeekStart, startOfWeek]);

	// Scroll to selected date only when selectedDate changes (e.g. returning from full edit)
	useEffect(() => {
		if (calendarRef.current && selectedDate) {
			setTimeout(() => {
				// Get the calendar's root element from the container
				const calendarEl = document.querySelector(
					'.fullcalendar-schedule-container .fc'
				) as HTMLElement;

				if (!calendarEl) {
					console.log('Calendar element not found');
					return;
				}

				// For timeline views, we need to scroll the timeline body lanes container
				// This is where the actual events are rendered and needs horizontal scrolling
				let timelineElement = calendarEl.querySelector(
					'.fc-timeline-body .fc-timeline-lane-frame'
				) as HTMLElement;

				if (!timelineElement) {
					// Try finding the scrollable container for timeline lanes
					timelineElement = calendarEl.querySelector(
						'.fc-timeline-body .fc-scroller'
					) as HTMLElement;
				}

				if (!timelineElement) {
					// Try the main timeline scroller
					timelineElement = calendarEl.querySelector(
						'.fc-scroller.fc-scroller-liquid-absolute'
					) as HTMLElement;
				}

				// console.log('Scrolling to date:', selectedDate, {
				// 	found: !!timelineElement,
				// 	businessWeekStart,
				// 	elementClass: timelineElement?.className,
				// 	scrollLeft: timelineElement?.scrollLeft,
				// 	scrollWidth: timelineElement?.scrollWidth,
				// 	clientWidth: timelineElement?.clientWidth,
				// 	parentElement: timelineElement?.parentElement?.className
				// });

				if (timelineElement && businessWeekStart) {
					// Calculate the horizontal scroll position based on date AND time
					const weekStart = moment(businessWeekStart);
					const targetDate = moment(selectedDate);
					const daysDiff = targetDate.diff(weekStart, 'days');

					// Find the actual scrollable parent if current element isn't scrollable
					let scrollableElement = timelineElement;
					if (timelineElement.scrollWidth <= timelineElement.clientWidth) {
						// Try to find parent that's scrollable
						let parent = timelineElement.parentElement;
						while (parent && parent.scrollWidth <= parent.clientWidth) {
							parent = parent.parentElement;
							if (parent?.classList.contains('fc-scroller')) {
								scrollableElement = parent as HTMLElement;
								break;
							}
						}
						// console.log('Found scrollable parent:', scrollableElement.className, {
						// 	scrollWidth: scrollableElement.scrollWidth,
						// 	clientWidth: scrollableElement.clientWidth
						// });
					}

					// Calculate scroll position based on proportion of the week
					// The business week is 7 days, so we calculate the position proportionally
					const totalDaysInWeek = 7;
					const maxScrollLeft = scrollableElement.scrollWidth - scrollableElement.clientWidth;

					// Calculate the base scroll position for the day
					const dayScrollPosition = (daysDiff / totalDaysInWeek) * scrollableElement.scrollWidth;

					// In day view, also calculate time offset within the day
					// In week view, scroll to start of day only
					let timeOffset = 0;
					let hours = 0;
					let minutes = 0;
					let timeInHours = 0;

					if (calendarView === 'day') {
						// Get hours and minutes from the selected date
						hours = targetDate.hours();
						minutes = targetDate.minutes();
						timeInHours = hours + minutes / 60;

						// Each day takes up (1/7) of the total scroll width
						const dayWidth = scrollableElement.scrollWidth / totalDaysInWeek;

						// Time offset within the day (0-24 hours maps to 0-dayWidth pixels)
						timeOffset = (timeInHours / 24) * dayWidth;
					}

					// Combine day position and time offset (timeOffset is 0 in week view)
					const scrollLeft = dayScrollPosition + timeOffset;

					// Clamp to valid scroll range
					const clampedScrollLeft = Math.max(0, Math.min(scrollLeft, maxScrollLeft));

					// console.log('Scrolling to date and time:', {
					// 	calendarView,
					// 	daysDiff,
					// 	hours,
					// 	minutes,
					// 	timeInHours,
					// 	totalDaysInWeek,
					// 	scrollWidth: scrollableElement.scrollWidth,
					// 	clientWidth: scrollableElement.clientWidth,
					// 	dayScrollPosition,
					// 	timeOffset,
					// 	maxScrollLeft,
					// 	calculatedScroll: scrollLeft,
					// 	clampedScrollLeft,
					// 	currentScrollLeft: scrollableElement.scrollLeft
					// });

					// Scroll to the exact date and time position
					scrollableElement.scrollLeft = clampedScrollLeft;
				}
			}, 1000);
		}
	}, [selectedDate, businessWeekStart, calendarView]);

	// Change view when calendarView changes
	useEffect(() => {
		if (calendarRef.current) {
			const calendarApi = calendarRef.current.getApi();
			// Both day and week views show the entire business week
			// The difference is in slot duration (configured separately)
			const targetView = 'resourceTimelineWeek';
			if (calendarApi.view.type !== targetView) {
				calendarApi.changeView(targetView);
			}
		}
	}, [calendarView]);

	useEffect(() => {
		setIsFullscreenSupported(Boolean(document.fullscreenEnabled));

		const onFullscreenChange = () => {
			const container = containerRef.current;
			const activeElement = document.fullscreenElement;
			setIsFullscreen(Boolean(container && activeElement && container.contains(activeElement)));
		};

		document.addEventListener('fullscreenchange', onFullscreenChange);
		return () => {
			document.removeEventListener('fullscreenchange', onFullscreenChange);
		};
	}, []);

	useEffect(() => {
		const container = containerRef.current;
		const toolbar = toolbarRef.current;
		if (!container || !toolbar) return;

		const syncStickyToolbarOffset = () => {
			container.style.setProperty('--schedule-sticky-toolbar-offset', `${toolbar.offsetHeight}px`);
		};

		syncStickyToolbarOffset();

		const resizeObserver =
			typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncStickyToolbarOffset) : null;
		resizeObserver?.observe(toolbar);
		window.addEventListener('resize', syncStickyToolbarOffset);

		return () => {
			resizeObserver?.disconnect();
			window.removeEventListener('resize', syncStickyToolbarOffset);
		};
	}, [renderHeaderOverride, calendarView, isFullscreen, isTabletViewport, canManageShifts]);

	// Handle event click
	const handleEventClick = useCallback(
		(info: any) => {
			if (calendarType !== 'schedule') return;

			// FullCalendar listens natively on the calendar root, so React's stopPropagation in the
			// shift card's own controls (copy/delete/status) can't stop eventClick from firing.
			const target = info.jsEvent?.target as Element | null;
			if (target?.closest?.(SHIFT_CARD_CONTROL_SELECTOR)) return;

			const originalEvent = info.event.extendedProps;
			if (originalEvent?.isTimeOff) {
				onTimeOffClick?.(originalEvent);
				return;
			}

			if (!canInteractWithShifts || !onEditShift) return;
			onEditShift(originalEvent);
		},
		[canInteractWithShifts, onEditShift, onTimeOffClick, calendarType]
	);

	// Handle date click (cell click)
	const handleDateClick = useCallback(
		(info: any) => {
			if (!canInteractWithShifts || calendarType !== 'schedule') return;

			// Drag-to-create is handled via select; single clicks use default shift length
			if (!onCreateShiftByDrag) return;

			const start = info.date;
			const clickEnd = moment(start)
				.add(dragTimeStep || 15, 'minutes')
				.toDate();
			const end = resolveShiftEndTime(start, clickEnd, {
				defaultShiftLengthHours,
				minShiftLength,
			});
			const resourceId = info.resource?.id;

			if (
				!resourceId ||
				isDepartmentHeaderResourceId(resourceId) ||
				isFullscreenSpacerResourceId(resourceId)
			)
				return;

			onCreateShiftByDrag({
				start,
				end,
				resourceId: String(resourceId),
			});
		},
		[
			canInteractWithShifts,
			calendarType,
			onCreateShiftByDrag,
			dragTimeStep,
			defaultShiftLengthHours,
			minShiftLength,
		]
	);

	const handleSelect = useCallback(
		(selectInfo: any) => {
			if (!canInteractWithShifts || calendarType !== 'schedule' || !onCreateShiftByDrag) {
				selectInfo.view.calendar.unselect();
				return;
			}

			const resourceId = selectInfo.resource?.id;
			if (
				!resourceId ||
				isDepartmentHeaderResourceId(resourceId) ||
				isFullscreenSpacerResourceId(resourceId)
			) {
				selectInfo.view.calendar.unselect();
				return;
			}

			onCreateShiftByDrag({
				start: selectInfo.start,
				end: selectInfo.end,
				resourceId: String(resourceId),
			});

			selectInfo.view.calendar.unselect();
		},
		[canInteractWithShifts, calendarType, onCreateShiftByDrag]
	);

	const handleSelectAllow = useCallback(
		(selectInfo: any) => {
			if (isDepartmentHeaderResourceId(selectInfo.resource?.id)) return false;
			if (isFullscreenSpacerResourceId(selectInfo.resource?.id)) return false;
			if (!isWeekLikeView) return true;
			const startDay = moment(selectInfo.start).format('YYYY-MM-DD');
			const endDay = moment(selectInfo.end).subtract(1, 'millisecond').format('YYYY-MM-DD');
			return startDay === endDay;
		},
		[isWeekLikeView]
	);

	const handleEventAllow = useCallback(
		(dropInfo: any, draggedEvent: any) => {
			if (draggedEvent?.extendedProps?.isTimeOff) return false;
			if (isDepartmentHeaderResourceId(dropInfo.resource?.id)) return false;
			if (isFullscreenSpacerResourceId(dropInfo.resource?.id)) return false;

			// Week view: allow moving shifts between employee rows and across days
			if (isWeekLikeView) {
				return Boolean(dropInfo.resource?.id);
			}

			// Day view: block row changes (job code is tied to the row)
			const oldResourceId = draggedEvent.getResources()[0]?.id;
			const newResourceId = dropInfo.resource?.id;
			return oldResourceId === newResourceId;
		},
		[isWeekLikeView]
	);

	// Week view renders shifts as full-day blocks; day view stacks multiple shifts per job code row.
	// Strict no-overlap rejects most valid drag/resize operations when other shifts exist nearby.
	const handleEventOverlap = useCallback((stillEvent: any) => {
		// Block dropping/resizing onto approved time-off blocks only.
		return !stillEvent.extendedProps?.isTimeOff;
	}, []);

	const getResourceRow = useCallback((element: Element): HTMLElement | null => {
		const tableRow = element.closest('tr');
		if (tableRow instanceof HTMLElement) return tableRow;

		const roleRow = element.closest('[role="row"]');
		if (roleRow instanceof HTMLElement) return roleRow;

		return null;
	}, []);

	const getResourceIdFromRow = useCallback((row: HTMLElement): string | null => {
		if (row.dataset.resourceId) {
			return row.dataset.resourceId;
		}

		const resourceCell = row.querySelector('[data-resource-id]');
		return resourceCell?.getAttribute('data-resource-id') ?? null;
	}, []);

	const bindResourceRowReorder = useCallback(
		(handle: HTMLElement, resourceId: string): (() => void) | undefined => {
			if (handle.dataset.scheduleReorderBound === 'true') return undefined;

			const row = getResourceRow(handle);
			if (!row) return undefined;

			handle.dataset.scheduleReorderBound = 'true';
			handle.setAttribute('draggable', 'true');

			const onDragStart = (event: DragEvent) => {
				event.stopPropagation();
				if (!event.dataTransfer) return;

				event.dataTransfer.setData('text/plain', resourceId);
				event.dataTransfer.effectAllowed = 'move';
				row.classList.add('is-resource-row-dragging');
			};

			const onDragEnd = (event: DragEvent) => {
				event.stopPropagation();
				row.classList.remove('is-resource-row-dragging');
				containerRef.current
					?.querySelectorAll('.is-resource-row-drag-over')
					.forEach((element) => element.classList.remove('is-resource-row-drag-over'));
			};

			handle.addEventListener('dragstart', onDragStart);
			handle.addEventListener('dragend', onDragEnd);

			return () => {
				handle.removeEventListener('dragstart', onDragStart);
				handle.removeEventListener('dragend', onDragEnd);
				delete handle.dataset.scheduleReorderBound;
				handle.removeAttribute('draggable');
				row.classList.remove('is-resource-row-dragging');
			};
		},
		[getResourceRow]
	);

	const bindResourceRowDropZone = useCallback(
		(dropZone: HTMLElement): (() => void) | undefined => {
			if (dropZone.dataset.scheduleDropZoneBound === 'true') return undefined;

			dropZone.dataset.scheduleDropZoneBound = 'true';

			const onDragOver = (event: DragEvent) => {
				if (!event.dataTransfer?.types.includes('text/plain')) return;

				event.preventDefault();
				event.stopPropagation();
				event.dataTransfer.dropEffect = 'move';

				const row = getResourceRow(event.target as Element);
				if (!row) return;

				containerRef.current?.querySelectorAll('.is-resource-row-drag-over').forEach((element) => {
					if (element !== row) element.classList.remove('is-resource-row-drag-over');
				});
				row.classList.add('is-resource-row-drag-over');
			};

			const onDragLeave = (event: DragEvent) => {
				const row = getResourceRow(event.target as Element);
				if (!row) return;

				const related = event.relatedTarget as Node | null;
				if (related && row.contains(related)) return;

				row.classList.remove('is-resource-row-drag-over');
			};

			const onDrop = (event: DragEvent) => {
				event.preventDefault();
				event.stopPropagation();

				const draggedId = event.dataTransfer?.getData('text/plain');
				const row = getResourceRow(event.target as Element);
				if (!draggedId || !row) return;

				const targetId = getResourceIdFromRow(row);
				row.classList.remove('is-resource-row-drag-over');
				if (
					!targetId ||
					targetId === 'open-shifts' ||
					isFullscreenSpacerResourceId(targetId) ||
					draggedId === targetId
				)
					return;

				handleEmployeeRowReorderRef.current(draggedId, targetId);
			};

			dropZone.addEventListener('dragover', onDragOver);
			dropZone.addEventListener('dragleave', onDragLeave);
			dropZone.addEventListener('drop', onDrop);

			return () => {
				dropZone.removeEventListener('dragover', onDragOver);
				dropZone.removeEventListener('dragleave', onDragLeave);
				dropZone.removeEventListener('drop', onDrop);
				delete dropZone.dataset.scheduleDropZoneBound;
			};
		},
		[getResourceIdFromRow, getResourceRow]
	);

	// Handle event drop (drag and drop)
	const handleEventDrop = useCallback(
		(info: any) => {
			if (!canInteractWithShifts) {
				info.revert();
				return;
			}

			if (info.event?.extendedProps?.isTimeOff) {
				info.revert();
				return;
			}

			const oldResourceId = info.oldResource?.id;
			const newResourceId = info.event.getResources()[0]?.id;
			if (!isWeekLikeView && oldResourceId && newResourceId && oldResourceId !== newResourceId) {
				info.revert();
				return;
			}

			const event = info.event;
			const extendedProps = event.extendedProps;

			let newStart: Date;
			let newEnd: Date;

			// For week view, use actual times and apply the day change
			if (isWeekLikeView) {
				({ start: newStart, end: newEnd } = getWeekViewDropTimes(
					extendedProps.actualStart || extendedProps.startAt || extendedProps.start,
					extendedProps.actualEnd || extendedProps.endAt || extendedProps.end,
					info.oldEvent.start,
					event.start
				));
			} else {
				// For day view, use the new times directly from FullCalendar
				newStart = event.start;
				newEnd = event.end;
			}

			// Get the new resource ID (could be employee or jobCode depending on view)
			const droppedResourceId = event.getResources()[0]?.id;

			if (onShiftDragUpdate) {
				const resourceChanged =
					oldResourceId && droppedResourceId && oldResourceId !== droppedResourceId;
				const droppedEmployeeId = parseWeekViewEmployeeId(droppedResourceId);

				// Pass the complete event with updated times and resource
				const didUpdate = onShiftDragUpdate({
					event: {
						...extendedProps,
						originalStartAt: extendedProps.startAt || extendedProps.start, // Preserve original date for metrics
						originalEndAt: extendedProps.endAt || extendedProps.end, // Preserve original date for metrics
						start: newStart,
						end: newEnd,
						startAt: newStart, // Ensure startAt is also updated
						endAt: newEnd, // Ensure endAt is also updated
						resource: droppedResourceId,
						...(isWeekLikeView && resourceChanged
							? {
									employee: droppedEmployeeId ? { id: droppedEmployeeId } : null,
								}
							: {
									employee: extendedProps.employee,
									jobCode: extendedProps.jobCode,
								}),
					},
				});

				if (didUpdate === false) {
					info.revert();
				}
			}
		},
		[canInteractWithShifts, onShiftDragUpdate, isWeekLikeView]
	);

	// Handle event resize
	const handleEventResize = useCallback(
		(info: any) => {
			if (!canInteractWithShifts) {
				info.revert();
				return;
			}

			if (info.event?.extendedProps?.isTimeOff) {
				info.revert();
				return;
			}

			const event = info.event;
			const extendedProps = event.extendedProps;

			// Resizing only works in day view (disabled in week view via eventDurationEditable)
			// Use the new times directly from FullCalendar
			const newStart = event.start;
			const newEnd = event.end;

			if (onShiftDragUpdate) {
				const didUpdate = onShiftDragUpdate({
					event: {
						...extendedProps,
						originalStartAt: extendedProps.startAt || extendedProps.start, // Preserve original date for metrics
						originalEndAt: extendedProps.endAt || extendedProps.end, // Preserve original date for metrics
						start: newStart,
						end: newEnd,
						startAt: newStart, // Ensure startAt is also updated
						endAt: newEnd, // Ensure endAt is also updated
						resource: event.getResources()[0]?.id,
					},
				});

				if (didUpdate === false) {
					info.revert();
				}
			}
		},
		[canInteractWithShifts, onShiftDragUpdate]
	);

	const isCalendarLoading = isLoadingUsersWorkplace || isShiftsLoading;

	useEffect(() => {
		if (!canReorderEmployeeRows || isCalendarLoading || !containerRef.current) return;

		const cleanups: Array<() => void> = [];
		const timer = window.setTimeout(() => {
			const container = containerRef.current;
			if (!container) return;

			container
				.querySelectorAll<HTMLElement>('.schedule-resource-drag-handle[data-resource-id]')
				.forEach((handle) => {
					const resourceId = handle.dataset.resourceId;
					if (!resourceId || resourceId === 'open-shifts') return;

					const cleanup = bindResourceRowReorder(handle, resourceId);
					if (cleanup) cleanups.push(cleanup);
				});

			const dropZones = [
				container.querySelector<HTMLElement>('.fc-datagrid-body'),
				container.querySelector<HTMLElement>('.fc-timeline-body'),
			].filter(Boolean) as HTMLElement[];

			dropZones.forEach((dropZone) => {
				const cleanup = bindResourceRowDropZone(dropZone);
				if (cleanup) cleanups.push(cleanup);
			});
		}, 250);

		return () => {
			window.clearTimeout(timer);
			cleanups.forEach((cleanup) => cleanup());
		};
	}, [
		bindResourceRowDropZone,
		bindResourceRowReorder,
		canReorderEmployeeRows,
		isCalendarLoading,
		fullCalendarResources,
	]);

	return (
		<div
			ref={containerRef}
			className={`fullcalendar-schedule-container ${isWeekLikeView ? 'is-week-view' : 'is-day-view'} ${isFullscreen ? 'is-fullscreen' : ''}`}
		>
			<div ref={toolbarRef} className="fullcalendar-schedule-toolbar">
				{renderHeaderOverride ? (
					renderHeaderOverride()
				) : (
					<ScheduleEventsHeader
						workplacesData={workplacesData}
						workplaceId={workplaceId}
						onSwitchCalendarType={setCalendarType}
						onAddShift={() =>
							openAddShiftModal({
								date: selectedDate || new Date(),
								resource: null,
							})
						}
						onPublish={onPublish}
						canManageShifts={canManageShifts}
						isShiftActionsEnabled={isShiftActionsEnabled}
						isFullscreenSupported={isFullscreenSupported}
						isFullscreen={isFullscreen}
						onToggleFullscreen={toggleFullscreen}
						headerStartContent={headerStartContent}
					/>
				)}
			</div>

			<div ref={calendarBodyRef} className="fullcalendar-schedule-body">
				{isCalendarLoading && (
					<div className="fullcalendar-schedule-loading">
						<CircularProgress size={40} />
					</div>
				)}

				{!isCalendarLoading && (
					<FullCalendar
						key={`${startOfWeek || DEFAULT_START_OF_WEEK}-${calendarView}`}
						ref={calendarRef}
						plugins={fullCalendarPlugins}
						schedulerLicenseKey={FULLCALENDAR_LICENSE_KEY}
						{...fullCalendarLegacyClassOptions}
						resourceLaneClass={(arg) =>
							joinFcClass(
								'fc-timeline-lane',
								'fc-timeline-lane-frame',
								getDepartmentHeaderClass(arg)
							)
						}
						resourceCellClass={(arg) =>
							joinFcClass(
								'fc-datagrid-cell',
								'fc-resource',
								'fc-datagrid-cell-frame',
								getDepartmentHeaderClass(arg)
							)
						}
						initialView="resourceTimelineWeek"
						initialDate={calendarInitialDate}
						headerToolbar={false}
						timeZone="local"
						resources={fullCalendarResources}
						events={fullCalendarEvents}
						filterResourcesWithEvents={false}
						editable={canInteractWithShifts && calendarType === 'schedule'}
						eventStartEditable={canInteractWithShifts && calendarType === 'schedule'}
						eventDurationEditable={
							canInteractWithShifts && calendarType === 'schedule' && calendarView === 'day'
						}
						eventResourceEditable={canInteractWithShifts && calendarType === 'schedule'}
						droppable={canInteractWithShifts && calendarType === 'schedule'}
						selectable={canInteractWithShifts && calendarType === 'schedule'}
						selectMinDistance={8}
						selectMirror={true}
						dayMaxEvents={true}
						slotDuration={calendarView === 'day' ? '01:00:00' : '24:00:00'}
						slotHeaderInterval={calendarView === 'day' ? '01:00:00' : '24:00:00'}
						{...(calendarView === 'day' && {
							slotHeaderContent: (arg: { date: Date; level: number; text: string }) => {
								// v7: level 0 = bottom-most (hours); higher levels = coarser (day labels)
								if (arg.level === 0) {
									return formatSchedulerTime(arg.date);
								}
								return arg.text;
							},
						})}
						slotHeaderFormat={
							calendarView === 'day'
								? [
										// Top row: day labels spanning each day
										{ day: 'numeric', weekday: 'short', month: 'long' },
										// Bottom row: hourly labels
										{
											hour: 'numeric',
											minute: '2-digit',
											omitZeroMinute: true,
											meridiem: 'short',
										},
									]
								: [
										{
											day: 'numeric',
											weekday: 'short',
											month: 'long',
										},
									]
						}
						resourceColumnsWidth={compactSizing.resourceAreaWidth}
						resourceColumnHeaderContent={calendarView === 'day' ? 'Job Codes' : 'Employees'}
						resourceOrder="sortOrder"
						height={isFullscreen ? '100%' : 'auto'}
						{...(isFullscreen ? {} : { contentHeight: 'auto' })}
						tableHeaderSticky={isFullscreen}
						{...(isFullscreen ? {} : { aspectRatio: 2 })}
						slotMinWidth={compactSizing.slotMinWidth}
						eventMinWidth={compactSizing.eventMinWidth}
						snapDuration={calendarSnapDuration}
						eventOverlap={handleEventOverlap}
						displayEventTime={calendarView === 'day'}
						displayEventEnd={calendarView === 'day'}
						slotMinTime={'00:00:00'}
						slotMaxTime="24:00:00"
						scrollTime={workplaceWorkingHoursStart || startOfDay || '08:00:00'}
						navLinks={false}
						firstDay={firstDayIndex}
						validRange={visibleRange}
						eventClick={handleEventClick}
						dateClick={handleDateClick}
						select={handleSelect}
						selectAllow={handleSelectAllow}
						eventAllow={handleEventAllow}
						eventDragStart={handleEventInteractionStart}
						eventDragStop={handleEventInteractionStop}
						eventDrop={handleEventDrop}
						eventResizeStart={handleEventInteractionStart}
						eventResizeStop={handleEventInteractionStop}
						eventResize={handleEventResize}
						eventContent={(arg) => {
							const event = arg.event.extendedProps;
							const isTimeOff = Boolean((event as any)?.isTimeOff);
							return (
								<div className={`fc-event-custom${isTimeOff ? ' fc-event-custom--time-off' : ''}`}>
									<ScheduleEvent
										calendarType={calendarType}
										calendarView={calendarView}
										data={{ original: event } as any}
										onDeleteClick={onDeleteShift}
										onCopyClick={canInteractWithShifts ? onCopyShift : undefined}
										canDeleteShifts={canDeleteShifts}
										canManageAllRequests={canManageAllRequests}
										currentUserId={scheduleUserId}
									/>
								</div>
							);
						}}
						resourceCellContent={(arg) => {
							if (arg.field && arg.field !== 'title') return undefined;
							if (
								!arg.resource ||
								isFullscreenSpacerResourceId(arg.resource.id) ||
								arg.resource.extendedProps?.isFullscreenSpacer
							) {
								return null;
							}
							if (String(arg.resource.id) === 'timeoff') {
								return (
									<div className="md-work-week-cont is-timeoff-resource">
										<div className="md-work-week-name">
											<span className="jobcode-resource">Time Off</span>
										</div>
									</div>
								);
							}
							const resource = arg.resource.extendedProps;
							return (
								<ScheduleResourceComponent
									resource={resource as any}
									calendarType={calendarType}
									weekStart={businessWeekStart}
									showDragHandle={
										canReorderEmployeeRows &&
										!isDepartmentHeaderResourceId(arg.resource.id) &&
										String(arg.resource.id) !== 'open-shifts'
									}
								/>
							);
						}}
					/>
				)}
			</div>
		</div>
	);
};

export default FullCalendarSchedule;
